import { useResource, money, monthYear } from '../../lib/hooks';
import { LocalDataTable } from '../../components/LocalDataTable';

/**
 * Teacher and Student self-service.
 *
 * None of these are reachable today: the token endpoint refuses Teacher and Student
 * accounts, matching the legacy login page, at the client's request. They exist so the
 * structure stays uniform — the endpoints behind them are ported and role-guarded, so
 * switching the feature back on is a change to AuthService.AllowedRoles and nothing else.
 *
 * See KnowledgeBase/findings/disabled-features.md.
 */

function DisabledNotice() {
  return (
    <div
      className="card"
      style={{ borderLeft: '4px solid var(--warning)', marginBottom: 20 }}
    >
      <strong>Self-service is currently switched off.</strong>
      <p style={{ color: 'var(--text-muted)', margin: '4px 0 0' }}>
        Teacher and Student sign-in was disabled at the client's request, so this page is not
        reachable in normal use. It is kept working so the feature can be restored without a
        rewrite.
      </p>
    </div>
  );
}

export function MyInfoPage() {
  const student = useResource<{
    id: number; name: string; fatherName: string; schoolName?: string;
    studentMobileNo?: string; parentMobileNo?: string; studentEmail?: string;
    grade?: { description: string };
  }>('/api/students/me');

  return (
    <>
      <div className="page__header">
        <div><h2 className="page__title">My information</h2></div>
      </div>

      <DisabledNotice />

      <div className="card card--md">
        {student.loading && <p>Loading…</p>}
        {student.error && <p className="field__error">{student.error}</p>}

        {student.data && (
          <dl style={{ display: 'grid', gridTemplateColumns: 'auto 1fr', gap: '8px 20px', margin: 0 }}>
            <dt style={{ color: 'var(--text-muted)' }}>Student ID</dt><dd style={{ margin: 0 }}>STD-{student.data.id}</dd>
            <dt style={{ color: 'var(--text-muted)' }}>Name</dt><dd style={{ margin: 0 }}>{student.data.name}</dd>
            <dt style={{ color: 'var(--text-muted)' }}>Father</dt><dd style={{ margin: 0 }}>{student.data.fatherName}</dd>
            <dt style={{ color: 'var(--text-muted)' }}>Grade</dt><dd style={{ margin: 0 }}>{student.data.grade?.description ?? '—'}</dd>
            <dt style={{ color: 'var(--text-muted)' }}>School</dt><dd style={{ margin: 0 }}>{student.data.schoolName ?? '—'}</dd>
            <dt style={{ color: 'var(--text-muted)' }}>Mobile</dt><dd style={{ margin: 0 }}>{student.data.studentMobileNo ?? '—'}</dd>
            <dt style={{ color: 'var(--text-muted)' }}>Email</dt><dd style={{ margin: 0 }}>{student.data.studentEmail ?? '—'}</dd>
          </dl>
        )}
      </div>
    </>
  );
}

export function MyEnrolledCoursesPage() {
  const enrolled = useResource<{
    id: number;
    studentCourseFees?: { amount: number };
    course?: { grade?: { description: string }; subject?: { description: string }; groupName?: string };
  }[]>('/api/enrollment/mine');

  return (
    <>
      <div className="page__header">
        <div><h2 className="page__title">My enrolled courses</h2></div>
      </div>

      <DisabledNotice />

      <LocalDataTable
        rows={enrolled.data ?? []}
        loading={enrolled.loading}
        error={enrolled.error}
        rowKey={(r) => r.id}
        emptyMessage="No enrolled courses."
        columns={[
          { key: 'grade', header: 'Grade', render: (r) => r.course?.grade?.description ?? '—' },
          { key: 'subject', header: 'Subject', render: (r) => r.course?.subject?.description ?? '—' },
          { key: 'group', header: 'Group', render: (r) => r.course?.groupName ?? '—' },
          {
            key: 'fee', header: 'Monthly fee', align: 'right',
            render: (r) => <span className="money">{money(r.studentCourseFees?.amount)}</span>,
          },
        ]}
      />
    </>
  );
}

export function MyTestScoresPage() {
  const scores = useResource<{
    id: number; obtainMarks: number; remarks: string;
    test?: { id: number; topic: string; totalMarks: number; dateTime: string };
  }[]>('/api/test-scores/mine');

  return (
    <>
      <div className="page__header">
        <div><h2 className="page__title">My test scores</h2></div>
      </div>

      <DisabledNotice />

      <LocalDataTable
        rows={scores.data ?? []}
        loading={scores.loading}
        error={scores.error}
        rowKey={(r) => r.id}
        emptyMessage="No scores recorded."
        columns={[
          { key: 'test', header: 'Test', render: (r) => `T-${r.test?.id}` },
          { key: 'topic', header: 'Topic', render: (r) => r.test?.topic },
          {
            key: 'date', header: 'Date',
            render: (r) => (r.test?.dateTime ? new Date(r.test.dateTime).toLocaleDateString() : '—'),
          },
          { key: 'obtained', header: 'Obtained', align: 'right', render: (r) => r.obtainMarks },
          { key: 'total', header: 'Total', align: 'right', render: (r) => r.test?.totalMarks },
          { key: 'remarks', header: 'Remarks', render: (r) => r.remarks ?? '—' },
        ]}
      />
    </>
  );
}

export function MyCoursesPage() {
  const courses = useResource<{
    id: number; groupName: string;
    grade?: { description: string }; subject?: { description: string };
    courseSchedules?: { courseScheduleId: number; day: string; startTime: string; endTime: string; roomNo: string }[];
  }[]>('/api/courses/mine');

  return (
    <>
      <div className="page__header">
        <div><h2 className="page__title">My courses</h2></div>
      </div>

      <DisabledNotice />

      <LocalDataTable
        rows={courses.data ?? []}
        loading={courses.loading}
        error={courses.error}
        rowKey={(r) => r.id}
        emptyMessage="No courses assigned."
        columns={[
          { key: 'id', header: 'Course', render: (r) => `C-${r.id}` },
          { key: 'grade', header: 'Grade', render: (r) => r.grade?.description ?? '—' },
          { key: 'subject', header: 'Subject', render: (r) => r.subject?.description ?? '—' },
          { key: 'group', header: 'Group', render: (r) => r.groupName },
          {
            key: 'schedule', header: 'Schedule',
            render: (r) => (r.courseSchedules ?? [])
              .map((x) => `${x.day} ${x.startTime}–${x.endTime}`).join(', ') || '—',
          },
        ]}
      />
    </>
  );
}

export function MyFeesPage() {
  const fees = useResource<{
    id: number; month: number | null; year: number | null;
    amount: number; paidAmount: number; isSubmitted: boolean;
    feesType?: { description: string };
    studentCourse?: { course?: { grade?: { description: string }; subject?: { description: string } } };
  }[]>('/api/studentfees/report/mine');

  return (
    <>
      <div className="page__header">
        <div><h2 className="page__title">My fees</h2></div>
      </div>

      <DisabledNotice />

      <LocalDataTable
        rows={fees.data ?? []}
        loading={fees.loading}
        error={fees.error}
        rowKey={(r) => r.id}
        emptyMessage="No fees recorded."
        columns={[
          { key: 'type', header: 'Type', render: (r) => r.feesType?.description ?? '—' },
          {
            key: 'course', header: 'Course',
            render: (r) => [r.studentCourse?.course?.grade?.description,
              r.studentCourse?.course?.subject?.description].filter(Boolean).join(' · ') || '—',
          },
          { key: 'period', header: 'Period', render: (r) => monthYear(r.month, r.year) },
          {
            key: 'amount', header: 'Amount', align: 'right',
            render: (r) => <span className="money">{money(r.amount)}</span>,
          },
          {
            key: 'paid', header: 'Paid', align: 'right',
            render: (r) => <span className="money">{money(r.paidAmount)}</span>,
          },
          {
            key: 'status', header: 'Status',
            render: (r) => (
              <span className={r.isSubmitted ? 'badge badge--paid' : 'badge badge--due'}>
                {r.isSubmitted ? 'Paid' : 'Due'}
              </span>
            ),
          },
        ]}
      />
    </>
  );
}
